/**
 * ОДИН способ выбрать картинку файлом. Раньше их было три (свой аватар,
 * аватар комнаты в настройках, аватар в шапке комнаты), все три написаны
 * по-разному — и ломались по-разному. Баг Антона 2026-09-24: «никакой
 * аватар нельзя загрузить», «кнопку вижу, но она не нажимается», «диалог
 * выбора файла не открывается».
 *
 * ВТОРОЙ ЗАХОД (24.09, после того как первая правка НЕ помогла).
 *
 * Первая правка лечила `display:none`. Замер это опроверг: скрепка в
 * композере прячет свой input ровно через `display:none`, зовёт
 * `.click()` — и файлы отправляются (десятки `media/v3/upload` в логах
 * Synapse за неделю из той же оболочки). Значит причина другая.
 *
 * Что сделано теперь:
 *
 *  1. КНОПКА ВЫБОРА ФАЙЛА НЕ БЛОКИРУЕТСЯ НИКОГДА. Все три места гасили её
 *     через `disabled={busy}`. Заблокированная кнопка не даёт ни клика,
 *     ни диалога, молча — это ДОСЛОВНО жалоба «вижу, но не нажимается».
 *     А `busy` там залипает навсегда, если запрос не вернулся: у
 *     `uploadContent` нет таймаута, зависшая загрузка оставляет `busy`
 *     true до перемонтирования компонента. Блокировать можно ОТПРАВКУ,
 *     но не ВЫБОР файла.
 *  2. `open()` больше не может стать пустышкой. Раньше `if (disabled)
 *     return;` глотал клик без единого следа.
 *  3. Между кликом человека и `.click()` НЕТ ни await, ни setTimeout, ни
 *     промиса — иначе движок теряет «пользовательский жест» и отказывает
 *     молча. Тот же синхронный порядок, что в рабочем композере.
 *  4. ВИДИМАЯ ДИАГНОСТИКА. Антон не может открыть консоль (Cmd+Option+I в
 *     его оболочке не работает), поэтому каждое состояние говорится
 *     человеку строкой прямо под кнопкой: нажатие, выбор файла, отправка,
 *     ошибка. Отдельно ловится случай «обработчик отработал, а система
 *     диалог не открыла» — именно он отличает мёртвую кнопку от запрета
 *     оболочки, и без него причину не измерить.
 *
 * Оставлено от первой правки (вреда нет, польза доказана отдельно):
 * `id` из `useId()` вместо жёсткого (дубли id при двух открытых
 * настройках) и сброс `value` ДО любого await (повторный выбор того же
 * файла иначе молчит).
 *
 * ТРЕТИЙ ЗАХОД (24.09, вечер): ВЕТКА ОБОЛОЧКИ.
 *
 * Замер показал, что в Mac-приложении не открывается сам системный
 * диалог: `<input type="file">` мгновенно возвращает «файл не выбран»,
 * Finder не появляется. Лечить это со стороны веба нечем — окно рисует
 * не веб. Поэтому оболочка 0.1.8 открывает окно САМА и выставляет мост
 * `window.algShell.pickFiles`.
 *
 * Здесь только развилка: есть мост — идём через него, нет моста —
 * прежний `input.click()` без единого изменения. Браузерное поведение
 * обязано остаться прежним, поэтому весь код ниже развилки не тронут:
 * сторож «диалог не открылся», `cancel`, сброс `value`, синхронный
 * `click()` — всё на своих местах и работает ровно как раньше.
 */
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type RefObject,
} from "react";
import { useTF } from "../i18n";
import { isShellFilePickerAvailable, pickFilesViaShell } from "../shell/desktopFilePicker";
import { shellPickErrorText } from "./shellPickerError";

/**
 * Визуально скрыт, но ОСТАЁТСЯ В РАСКЛАДКЕ.
 *
 * Композер доказал, что `display:none` тоже работает, так что это не
 * лекарство — но и не болезнь: 1×1px прозрачный элемент принимает
 * `.click()` заведомо во всех движках, а не «почти во всех».
 */
export const visuallyHiddenInput: CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  border: 0,
  opacity: 0,
  overflow: "hidden",
  clipPath: "inset(50%)",
  whiteSpace: "nowrap",
};

/** Что показать человеку строкой под кнопкой. */
export type PickerNote = {
  /** Ошибка красным, остальное приглушённым. */
  kind: "info" | "error";
  text: string;
  /** Меняется на каждое сообщение: нужен, чтобы перезапускать таймер скрытия. */
  seq: number;
};

export type ImagePicker = {
  /** Повесить на `<input type="file">`. */
  ref: RefObject<HTMLInputElement | null>;
  /** Уникальный id для пары input/label. Дубликатов больше не бывает. */
  id: string;
  /** Открыть системный диалог выбора файла. Синхронно, без условий. */
  open: () => void;
  /** Повесить на `onChange` того же input. */
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  /** Текущее сообщение человеку; null — молчим. */
  note: PickerNote | null;
};

/** Сколько держать сообщение на экране, мс. */
const HIDE_INFO_MS = 5000;
const HIDE_ERROR_MS = 10000;

/**
 * Сколько ждать признака того, что система ОТКРЫЛА диалог.
 *
 * Признаком считаем любое из трёх: `change` (файл выбран), `cancel`
 * (диалог закрыт без выбора) или потеря фокуса окном (диалог поверх).
 * Если за это время не пришло НИЧЕГО — обработчик отработал, а диалога
 * нет. Это факт, а не догадка, и именно он нужен для разбора.
 */
const NO_DIALOG_MS = 1500;

export function useImagePicker(opts: {
  /** Что делать с выбранной картинкой. */
  onFile: (file: File) => void | Promise<void>;
  /**
   * Необязательно: куда ПРОДУБЛИРОВАТЬ текст ошибки, если у экрана есть
   * своё штатное место для ошибок (общая плашка диалога). Там, где такого
   * места нет, строки под кнопкой достаточно — две одинаковые надписи
   * подряд только мешают.
   */
  onError?: (message: string) => void;
}): ImagePicker {
  const tf = useTF();
  const ref = useRef<HTMLInputElement | null>(null);
  const id = useId();
  const [note, setNote] = useState<PickerNote | null>(null);

  // Компонент может размонтироваться, пока идёт загрузка: обновлять
  // состояние после этого нельзя.
  const alive = useRef(true);
  const seq = useRef(0);
  // Признак «система ответила»: change / cancel / blur окна.
  const answered = useRef(false);
  const watchdog = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (watchdog.current) clearTimeout(watchdog.current);
    };
  }, []);

  const say = useCallback((kind: "info" | "error", text: string) => {
    if (!alive.current) return;
    seq.current += 1;
    setNote({ kind, text, seq: seq.current });
  }, []);

  // Сообщение гаснет само. Таймер перезапускается на каждом новом seq,
  // поэтому быстрая череда сообщений не гасит последнее раньше времени.
  useEffect(() => {
    if (!note) return;
    const ms = note.kind === "error" ? HIDE_ERROR_MS : HIDE_INFO_MS;
    const timer = setTimeout(() => {
      if (alive.current) setNote((cur) => (cur && cur.seq === note.seq ? null : cur));
    }, ms);
    return () => clearTimeout(timer);
  }, [note]);

  const stopWatchdog = useCallback(() => {
    answered.current = true;
    if (watchdog.current) {
      clearTimeout(watchdog.current);
      watchdog.current = null;
    }
  }, []);

  /**
   * Что делать с уже выбранным файлом. Одна и та же проверка и один и тот
   * же путь загрузки для обоих источников — штатного input и моста
   * оболочки. Вынесено из `onChange` БЕЗ изменения поведения.
   */
  const accept = useCallback(
    (file: File) => {
      if (!file.type.startsWith("image/")) {
        const msg = tf("ui.nuzhna_kartinka_png_jpeg_i_t_p");
        say("error", msg);
        opts.onError?.(msg);
        return;
      }

      say("info", tf("picker.chosen", { name: file.name }));
      void (async () => {
        try {
          await opts.onFile(file);
          say("info", tf("picker.done"));
        } catch (err) {
          // Место вызова тоже покажет ошибку своим способом; здесь она
          // нужна затем, что под кнопкой человек её увидит гарантированно.
          say("error", tf("picker.failed", { error: (err as Error)?.message ?? String(err) }));
        }
      })();
    },
    [opts, say, tf],
  );

  const open = useCallback(() => {
    // ВЕТКА ОБОЛОЧКИ. Окно открывает main-процесс, поэтому ни скрытый
    // input, ни сторож «диалог не открылся», ни сохранение
    // пользовательского жеста здесь не нужны: ограничения браузера на
    // этот путь не распространяются.
    if (isShellFilePickerAvailable()) {
      say("info", tf("picker.opening"));
      void (async () => {
        try {
          // `title` не передаём: у оболочки свой осмысленный заголовок окна,
          // а строка «Открываю выбор файла…» в шапке Finder выглядела бы дико.
          const files = await pickFilesViaShell({ kind: "avatar", multiple: false });
          if (!alive.current) return;
          // Закрыл окно, ничего не выбрав — ровно тот же текст, что и у
          // штатного `cancel`.
          if (!files || files.length === 0) {
            say("info", tf("picker.cancelled"));
            return;
          }
          accept(files[0]);
        } catch (err) {
          if (!alive.current) return;
          const msg = shellPickErrorText(err);
          say("error", msg);
          opts.onError?.(msg);
        }
      })();
      return;
    }

    const input = ref.current;
    if (!input) {
      // Единственный случай, когда клик физически некуда девать.
      say("error", tf("picker.noInput"));
      opts.onError?.(tf("picker.noInput"));
      return;
    }

    say("info", tf("picker.opening"));

    // Сторож «система не открыла диалог». Ставится ДО click(), снимается
    // первым же признаком жизни.
    answered.current = false;
    if (watchdog.current) clearTimeout(watchdog.current);
    const onBlur = () => stopWatchdog();
    // Событие `cancel` (человек закрыл диалог, ничего не выбрав) вешаем
    // здесь, а не пропом: React 19 его в типах input не объявляет, и
    // каждое место вызова всё равно забыло бы его подключить.
    const onNativeCancel = () => {
      stopWatchdog();
      say("info", tf("picker.cancelled"));
    };
    window.addEventListener("blur", onBlur, { once: true });
    input.addEventListener("cancel", onNativeCancel, { once: true });
    watchdog.current = setTimeout(() => {
      window.removeEventListener("blur", onBlur);
      watchdog.current = null;
      if (!answered.current) say("error", tf("picker.noDialog"));
    }, NO_DIALOG_MS);

    // СИНХРОННО, последним действием обработчика: ни await, ни
    // setTimeout, ни промиса между жестом человека и click() — иначе
    // движок теряет пользовательский жест. Тот же порядок, что у
    // рабочей скрепки в композере.
    input.click();
  }, [accept, opts, say, stopWatchdog, tf]);

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      stopWatchdog();
      const file = e.target.files?.[0];
      // Сброс ДО любого await: иначе повторный выбор того же файла не
      // порождает change, и человек снова видит «мёртвую» кнопку.
      e.target.value = "";
      if (!file) {
        say("info", tf("picker.cancelled"));
        return;
      }
      accept(file);
    },
    [accept, say, stopWatchdog, tf],
  );

  return { ref, id, open, onChange, note };
}
